import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, Field, InlineAlert, Input, Select, cn } from "../../shared/ui";
import type { PageResponse, WorkItemDetail, WorkItemSummary } from "../../shared/types/api";
import { WorkItemTypeBadge } from "./WorkItemTypeBadge";
import { LINK_CHOICES } from "./workItemVocabulary";

/**
 * Links two work items (§85).
 *
 * <p>Phrased as a sentence — "MB-14 blocks …" — because the direction is the part people get
 * wrong, and a dropdown of seven constants next to a search box does nothing to help. Reading the
 * finished sentence back before saving is the check.
 *
 * <p>The inverse forms are offered alongside the forward ones even though only four are stored.
 * Somebody looking at a blocked item thinks "this is blocked by that", not "let me go to the
 * other item and say it blocks this one".
 */
export function LinkDrawer({
  item,
  onClose,
  onSaved,
}: {
  item: WorkItemDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [linkType, setLinkType] = useState("BLOCKS");
  const [query, setQuery] = useState("");
  const [targetId, setTargetId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const candidates = useQuery({
    queryKey: ["work-items", "link-candidates", item?.projectId, query],
    queryFn: () => {
      const params = new URLSearchParams({ projectId: item?.projectId ?? "", size: "25" });
      if (query.trim()) {
        params.set("q", query.trim());
      }
      return api.get<PageResponse<WorkItemSummary>>(`/api/v1/work-items?${params}`);
    },
    enabled: Boolean(item),
  });

  useEffect(() => {
    if (item) {
      setLinkType("BLOCKS");
      setQuery("");
      setTargetId("");
      setError(null);
    }
  }, [item]);

  const submit = async () => {
    if (!item || !targetId) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/work-items/${item.id}/links`, { targetId, linkType });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not create the link.");
    } finally {
      setSaving(false);
    }
  };

  const options = (candidates.data?.content ?? []).filter(
    (candidate) => candidate.id !== item?.id,
  );
  const chosen = options.find((candidate) => candidate.id === targetId);
  const phrase = LINK_CHOICES.find((choice) => choice.value === linkType)?.label ?? "";

  return (
    <Drawer
      open={Boolean(item)}
      onClose={onClose}
      title="Link to another item"
      subtitle="A dependency, not a hierarchy — the two are kept separate on purpose."
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={saving} disabled={!targetId}>
            {saving ? "Linking…" : "Create link"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {/* The sentence being built, so the direction can be read back before saving. */}
        <div className="rounded-lg border border-ink-200 bg-ink-50 px-4 py-3 text-sm">
          <span className="font-mono text-xs text-ink-600">{item?.itemKey}</span>{" "}
          <span className="font-medium text-brand-700">{phrase}</span>{" "}
          {chosen ? (
            <span className="font-mono text-xs text-ink-600">{chosen.itemKey}</span>
          ) : (
            <span className="text-ink-400">…</span>
          )}
        </div>

        <Field label="Relationship" required>
          <Select value={linkType} onChange={(event) => setLinkType(event.target.value)}>
            {LINK_CHOICES.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Find the other item" hint="Search by title or key within this project.">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Payment gateway"
          />
        </Field>

        <div className="max-h-72 overflow-y-auto rounded-lg border border-ink-200">
          {options.length === 0 ? (
            <p className="px-3 py-4 text-sm text-ink-500">
              Nothing matches. Links stay within a project.
            </p>
          ) : (
            options.map((candidate) => (
              <button
                key={candidate.id}
                type="button"
                onClick={() => setTargetId(candidate.id)}
                className={cn(
                  "flex w-full items-center gap-2 border-b border-ink-100 px-3 py-2 text-left text-sm transition-colors last:border-0",
                  candidate.id === targetId
                    ? "bg-brand-50 text-brand-800"
                    : "hover:bg-ink-50",
                )}
              >
                <WorkItemTypeBadge type={candidate.workItemType} />
                <span className="font-mono text-xs text-ink-500">{candidate.itemKey}</span>
                <span className="truncate">{candidate.title}</span>
              </button>
            ))
          )}
        </div>

        {linkType === "BLOCKS" || linkType === "IS_BLOCKED_BY" ? (
          <InlineAlert tone="info">
            A blocking loop is refused. If two items ended up waiting on each other, neither could
            ever start, so the database will not record it.
          </InlineAlert>
        ) : null}
      </div>
    </Drawer>
  );
}
